import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import XLSX from 'xlsx'

import { GET, httpOk } from 'helpers/api'
import { formatDate, notify } from 'helpers/helpers'

// Инвентаризация имущества по QR-наклейкам. Работает с ручным сканером (он «печатает» код
// и жмёт Enter) и с камерой телефона/ноутбука, если браузер умеет распознавать QR (BarcodeDetector).
// Итог: что нашли, чего нет на месте и что стоит не в своём филиале.
// Точка не выбирается: проверяем имущество торговой точки из шапки кабинета
function Inventory({ posId, openCard }) {
	const { t } = useTranslation()

	const [expected, setExpected] = useState([])
	const [found, setFound] = useState({})
	const [misplaced, setMisplaced] = useState([])
	const [unknown, setUnknown] = useState([])
	const [code, setCode] = useState('')
	const [cameraOn, setCameraOn] = useState(false)

	const inputRef = useRef()
	const videoRef = useRef()
	const streamRef = useRef(null)
	const timerRef = useRef(null)
	const lastCodeRef = useRef({ 'code': null, 'at': 0 })
	// Состояние для колбэка камеры: в замыкании setInterval оно иначе застывает на момент запуска
	const stateRef = useRef({})
	stateRef.current = { expected, found, misplaced, unknown }

	const cameraSupported = typeof window !== 'undefined' && 'BarcodeDetector' in window && !!navigator.mediaDevices

	async function getExpected(currentPosId = posId) {
		if (!currentPosId) return
		const response = await GET('/finance/fixed-assets?status=in_use&pos_id=' + currentPosId, {}, { loader: true })
		if (httpOk(response)) {
			setExpected(response.data.data)
			reset()
		}
	}

	function reset() {
		setFound({})
		setMisplaced([])
		setUnknown([])
		setCode('')
		if (inputRef.current) inputRef.current.focus()
	}

	async function processCode(rawCode) {
		const value = String(rawCode || '').trim()
		if (!value) return
		const upper = value.toUpperCase()
		const state = stateRef.current

		const item = state.expected.find(asset =>
			String(asset.inventory_number).toUpperCase() === upper || String(asset.serial_number || '').toUpperCase() === upper)
		if (item) {
			if (!state.found[item.id]) {
				setFound({ ...state.found, [item.id]: new Date() })
				notify(item.name, 'success')
			}
			return
		}

		if (state.misplaced.some(asset => String(asset.inventory_number).toUpperCase() === upper)
			|| state.unknown.includes(value)) {
			return
		}

		const response = await GET('/finance/fixed-assets/lookup?code=' + encodeURIComponent(value))
		if (httpOk(response) && response.data.asset) {
			setMisplaced([...stateRef.current.misplaced, response.data.asset])
			notify(t('fa_inventory_misplaced_one', { 'name': response.data.asset.name, 'pos': response.data.asset.pos_name }))
		} else {
			setUnknown([...stateRef.current.unknown, value])
			notify(t('fa_inventory_unknown_one', { 'code': value }))
		}
	}

	function submitCode(e) {
		e.preventDefault()
		processCode(code)
		setCode('')
	}

	async function startCamera() {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ 'video': { 'facingMode': 'environment' } })
			streamRef.current = stream
			setCameraOn(true)
			setTimeout(() => {
				if (!videoRef.current) return
				videoRef.current.srcObject = stream
				videoRef.current.play()
				const detector = new window.BarcodeDetector({ 'formats': ['qr_code', 'code_128', 'ean_13'] })
				timerRef.current = setInterval(async () => {
					if (!videoRef.current || videoRef.current.readyState < 2) return
					try {
						const codes = await detector.detect(videoRef.current)
						codes.forEach(item => {
							// Один и тот же QR камера видит много раз подряд — повтор глушим на 3 секунды
							const now = Date.now()
							if (lastCodeRef.current.code === item.rawValue && now - lastCodeRef.current.at < 3000) return
							lastCodeRef.current = { 'code': item.rawValue, 'at': now }
							processCode(item.rawValue)
						})
					} catch (error) {
						// кадр не распознался — ждём следующий
					}
				}, 400)
			}, 50)
		} catch (error) {
			notify(t('fa_camera_error'))
		}
	}

	function stopCamera() {
		if (timerRef.current) clearInterval(timerRef.current)
		if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop())
		streamRef.current = null
		setCameraOn(false)
	}

	function exportToExcel() {
		const sheet = [[t('fa_inventory_number'), t('name2'), t('status'), t('pos')]]
		expected.forEach(item => {
			sheet.push([item.inventory_number, item.name, found[item.id] ? t('fa_inventory_found') : t('fa_inventory_missing'), item.pos_name])
		})
		misplaced.forEach(item => {
			sheet.push([item.inventory_number, item.name, t('fa_inventory_misplaced'), item.pos_name])
		})
		unknown.forEach(value => {
			sheet.push([value, '', t('fa_inventory_unknown'), ''])
		})

		const ws = XLSX.utils.aoa_to_sheet(sheet)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'SheetJS')
		XLSX.writeFile(wb, t('fa_tab_inventory') + ' ' + formatDate(new Date()) + '.xlsx')
	}

	useEffect(() => {
		getExpected()
		return () => stopCamera()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const foundCount = Object.keys(found).length
	const missing = expected.filter(item => !found[item.id])

	return (
		<div className="card">
			<div className="card-body">
				<div className="text-muted mb-3">{t('fa_inventory_hint')}</div>
				<div className="row g-2 mb-3">
					<div className="col-md-6">
						<form onSubmit={submitCode} autoComplete="off">
							<input type="text" className="form-control" ref={inputRef} autoFocus
								placeholder={t('fa_inventory_scan_placeholder')}
								value={code}
								onChange={(e) => setCode(e.target.value)} />
						</form>
					</div>
					<div className="col-md-5 d-flex gap-2">
						{cameraSupported && !cameraOn &&
							<button className="btn btn-outline-primary" onClick={() => startCamera()}>
								<i className="uil uil-camera me-1"></i>{t('fa_camera')}
							</button>
						}
						{cameraOn &&
							<button className="btn btn-outline-danger" onClick={() => stopCamera()}>
								<i className="uil uil-camera-slash me-1"></i>{t('fa_camera_stop')}
							</button>
						}
						<button className="btn btn-outline-primary" onClick={() => exportToExcel()}>EXCEL</button>
						<button className="btn btn-outline-warning ms-auto" onClick={() => reset()}>{t('fa_inventory_reset')}</button>
					</div>
				</div>

				{cameraOn &&
					<div className="mb-3 text-center">
						<video ref={videoRef} muted playsInline style={{ 'width': '100%', 'maxWidth': '420px', 'borderRadius': '8px', 'background': '#000' }}></video>
					</div>
				}

				<div className="row g-2 mb-3">
					<div className="col-md-3">
						<div className="border rounded p-2">
							<div className="text-muted small">{t('fa_inventory_found')}</div>
							<div className="fs-4 fw-bold text-success">{foundCount} / {expected.length}</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="border rounded p-2">
							<div className="text-muted small">{t('fa_inventory_missing')}</div>
							<div className="fs-4 fw-bold text-danger">{missing.length}</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="border rounded p-2">
							<div className="text-muted small">{t('fa_inventory_misplaced')}</div>
							<div className="fs-4 fw-bold text-warning">{misplaced.length}</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="border rounded p-2">
							<div className="text-muted small">{t('fa_inventory_unknown')}</div>
							<div className="fs-4 fw-bold">{unknown.length}</div>
						</div>
					</div>
				</div>

				<div className="table-responsive">
					<table className="table table-bordered table-sm mb-0">
						<thead>
							<tr>
								<th>{t('fa_inventory_number')}</th>
								<th>{t('name2')}</th>
								<th>{t('pos')}</th>
								<th className="text-center">{t('status')}</th>
							</tr>
						</thead>
						<tbody>
							{misplaced.map(item => (
								<tr key={'m' + item.id} style={{ 'cursor': 'pointer' }} onClick={() => openCard(item.id)}>
									<td>{item.inventory_number}</td>
									<td>{item.name}</td>
									<td>{item.pos_name}</td>
									<td className="text-center bg-warning text-white">{t('fa_inventory_misplaced')}</td>
								</tr>
							))}
							{unknown.map(value => (
								<tr key={'u' + value}>
									<td>{value}</td>
									<td colSpan="2" className="text-muted">{t('fa_inventory_unknown_hint')}</td>
									<td className="text-center bg-secondary text-white">{t('fa_inventory_unknown')}</td>
								</tr>
							))}
							{expected.map(item => (
								<tr key={item.id} style={{ 'cursor': 'pointer' }} onClick={() => openCard(item.id)}>
									<td>{item.inventory_number}</td>
									<td>{item.name}</td>
									<td>{item.pos_name}</td>
									<td className={'text-center ' + (found[item.id] ? 'bg-success text-white' : 'bg-danger text-white')}>
										{found[item.id] ? t('fa_inventory_found') : t('fa_inventory_missing')}
									</td>
								</tr>
							))}
							{expected.length === 0 && misplaced.length === 0 &&
								<tr><td colSpan="4" className="text-center text-muted py-4">{t('fa_empty')}</td></tr>
							}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export default Inventory
